import { couponActivityPageList } from '@/api/domain/activity'
import { operateMaxShowLength } from '@/store/dict/config'
import moment from 'moment'

const statusMap = {
  0: '未开始',
  1: '进行中',
  2: '已过期',
  3: '已失效',
}

function formatTime(timestamp) {
  if (timestamp === null || timestamp === undefined || timestamp === '') return '--'
  const normalizedTimestamp = typeof timestamp === 'string' && /^\d+$/.test(timestamp)
    ? Number(timestamp)
    : timestamp
  const time = moment(normalizedTimestamp)
  if (!time.isValid()) return '--'
  return time.format('YYYY-MM-DD HH:mm:ss')
}

function getThresholdText(thresholdType, thresholdAmount) {
  if (Number(thresholdType) === 1) return '无门槛'
  if (thresholdAmount === null || thresholdAmount === undefined || thresholdAmount === '') return '--'
  return `满${thresholdAmount}元可用`
}

function formatReceiveTime(startTime, endTime) {
  const start = formatTime(startTime)
  const end = formatTime(endTime)
  if (start === '--' || end === '--') return '--'
  return `${start} ~ ${end}`
}

function formatUseTime(validityPeriod) {
  if (Number(validityPeriod) === 0) return '同领取有效期'
  if (!validityPeriod && validityPeriod !== 0) return '--'
  return `领取后${validityPeriod}天内有效`
}

async function getCouponActivityList(params = {}) {
  const response = await couponActivityPageList(params)
  const { code, data = {} } = response || {}
  if (code !== 1) return response

  const items = (data.items || []).map((item) => ({
    ...item,
    activityId: item.id,
    couponName: item.name || '--',
    threshold: getThresholdText(item.thresholdType, item.thresholdAmount),
    discountContent:
      item.discountAmount === null || item.discountAmount === undefined || item.discountAmount === ''
        ? '--'
        : `${item.discountAmount}元`,
    statusLabel: statusMap[item.couponTemplateState] || '--',
    validReceiveTime: formatReceiveTime(item.startTime, item.endTime),
    validUseTime: formatUseTime(item.validityPeriod),
    remainCount: item.leftNumber ?? '--',
    createTime: formatTime(item.createStart),
    updateTime: formatTime(item.gmtModified),
  }))

  return {
    ...response,
    data: {
      ...data,
      items,
      totalNum: data.totalNum || 0,
    },
  }
}

export const configDataFn = ({ goDataDetail, judgePermission, initialFormData = {} }) => {
  const canViewData = () => (
    typeof judgePermission === 'function'
      ? judgePermission('activity_coupon_grant_record_new')
      : false
  )

  const configData = {
    formData: {
      formProps: {
        ref: 'searchForm',
        model: {
          ...initialFormData,
        },
      },
      customizeReset: () => {
        const model = configData.formData?.formProps?.model || {}
        model.id = ''
      },
      list: [
        {
          el: 'input',
          itemConfig: {
            label: '活动ID',
            prop: 'id',
          },
          elConfig: {
            clearable: true,
            placeholder: '请输入活动ID',
          },
        },
        {
          el: 'input',
          itemConfig: {
            label: '优惠券名称',
            prop: 'activityCouponName',
          },
          elConfig: {
            clearable: true,
            placeholder: '请输入优惠券名称',
          },
        },
        {
          el: 'select',
          itemConfig: {
            label: '状态',
            prop: 'couponTemplateState',
          },
          elConfig: {
            options: {
              label: 'label',
              value: 'value',
              list: [
                { label: '全部', value: '' },
                { label: '未开始', value: 0 },
                { label: '进行中', value: 1 },
                { label: '已过期', value: 2 },
                { label: '已失效', value: 3 },
              ],
            },
          },
        },
        {
          el: 'date-picker',
          itemConfig: {
            label: '创建时间',
            prop: 'createTimeRange',
          },
          elConfig: {
            type: 'daterange',
            format: 'yyyy/MM/dd',
            'value-format': 'yyyy-MM-dd',
            'range-separator': '~',
            'start-placeholder': '请选择',
            'end-placeholder': '请选择',
          },
        },
        {
          el: 'date-picker',
          itemConfig: {
            label: '生效时间',
            prop: 'effectTimeRange',
          },
          elConfig: {
            type: 'daterange',
            format: 'yyyy/MM/dd',
            'value-format': 'yyyy-MM-dd',
            'range-separator': '~',
            'start-placeholder': '请选择',
            'end-placeholder': '请选择',
          },
        },
      ],
      dealWithSearch: (formData) => {
        const [createStartDate = '', createEndDate = ''] = formData.createTimeRange || []
        const [effectiveStartDate = '', effectiveEndDate = ''] = formData.effectTimeRange || []
        const params = {
          ...formData,
          createStart: createStartDate
            ? moment(createStartDate).startOf('day').valueOf()
            : '',
          createEnd: createEndDate
            ? moment(createEndDate).endOf('day').valueOf()
            : '',
          effectiveStart: effectiveStartDate
            ? moment(effectiveStartDate).startOf('day').valueOf()
            : '',
          effectiveEnd: effectiveEndDate
            ? moment(effectiveEndDate).endOf('day').valueOf()
            : '',
        }
        delete params.createTimeRange
        delete params.effectTimeRange
        return params
      },
    },
    isShowDepartment: false,
    tableData: {
      columns: [
        {
          name: 'activityId',
          columnProps: {
            label: '活动ID',
            prop: 'activityId',
            width: 90,
          },
        },
        {
          name: 'couponName',
          columnProps: {
            label: '优惠券名称',
            prop: 'couponName',
            width: 160,
          },
        },
        {
          name: 'threshold',
          columnProps: {
            label: '使用门槛',
            prop: 'threshold',
            width: 110,
          },
        },
        {
          name: 'discountContent',
          columnProps: {
            label: '优惠内容',
            prop: 'discountContent',
            width: 100,
          },
        },
        {
          name: 'statusLabel',
          columnProps: {
            label: '状态',
            prop: 'statusLabel',
            width: 90,
          },
        },
        {
          name: 'validReceiveTime',
          columnProps: {
            label: '领取有效期',
            prop: 'validReceiveTime',
            width: 240,
          },
        },
        {
          name: 'validUseTime',
          columnProps: {
            label: '使用有效期',
            prop: 'validUseTime',
            width: 130,
          },
        },
        {
          name: 'remainCount',
          columnProps: {
            label: '剩余',
            prop: 'remainCount',
            width: 90,
          },
        },
        {
          name: 'createTime',
          columnProps: {
            label: '创建时间',
            prop: 'createTime',
            width: 160,
          },
        },
        {
          name: 'updateTime',
          columnProps: {
            label: '修改时间',
            prop: 'updateTime',
            width: 160,
          },
        },
        {
          name: 'operate',
          columnProps: {
            label: '操作',
            prop: 'operate',
            fixed: 'right',
            minWidth: 70,
          },
          maxShowLength: operateMaxShowLength,
          type: 'operate',
          showColumnItem: canViewData,
          getOperateList: ({ row }) => {
            if (!canViewData()) return []
            return [
              {
                label: '数据',
                action: () => goDataDetail(row),
              },
            ]
          },
        },
      ],
      apiFn: getCouponActivityList,
    },
  }

  return configData
}
